/** @jsxImportSource @emotion/react */

import { formatErrorResponse } from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  useTheme,
  Snackbar,
  Modal,
  Button,
} from '@quickbit/qb-design-system';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMount, useUpdateEffect } from 'react-use';
import { useGetRecipients } from 'api/walletRecipientApi';
import { ContactStatus } from 'api/walletRecipientApi.types';
import { Layout, LayoutModal } from 'components';
import { Markdown } from 'components/Markdown';
import { useOrganisation } from 'contexts';
import { usePoll } from 'hooks/usePoll';

export const TravelRuleInfo = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { spacing, palette, sizing } = useTheme();
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [showSendError, setShowSendError] = useState(false);
  const web_url = process.env.REACT_APP_QB_WEB_URL || 'INVALID_URL';
  const travelRuleUrl = `${web_url}/contact-travel-rule-beneficiary-info/${contactId}${window.location.search}`;
  const {
    selectedMerchant: { merchantId },
  } = useOrganisation();
  const { refetch: refetchRecipients } = useGetRecipients({
    merchantId,
    currency: 'USDC',
  });

  if (!contactId) {
    throw new Error('Missing required parameters');
  }

  const openTravelRuleForm = () => {
    const openedWindow = window.open(travelRuleUrl);
    if (!openedWindow) {
      setPopupBlocked(true);
    }
  };

  useMount(() => {
    void startTravelRulePolling();
    openTravelRuleForm();
  });

  const checkTravelRuleSuccess = useCallback(
    (data?: { status: boolean }) => {
      if (data && data.status) {
        void navigate('/wallet/recipients');
        return true;
      }
      return null;
    },
    [navigate]
  );

  const getTravelRuleStatus = useCallback(async () => {
    const recipients = await refetchRecipients();
    const current = recipients.data?.find((recipient) => {
      if (recipient.uuid === contactId) return true;
    });
    if (current && current.status !== ContactStatus.PENDING_TRAVEL_RULE)
      return { status: true };
    return undefined;
  }, [refetchRecipients, contactId]);

  const {
    start: startTravelRulePolling,
    error: travelRuleInfoError,
    isPolling: isPollingTravelRule,
  } = usePoll({
    poll: getTravelRuleStatus,
    onPoll: checkTravelRuleSuccess,
  });

  useUpdateEffect(() => {
    setShowSendError(!!travelRuleInfoError);
  }, [travelRuleInfoError]);

  return (
    <LayoutModal>
      <Layout
        title="Add new wallet recipient - travel rule info"
        primaryButtonProps={{
          text: 'Add recipient',
          disabled: true,
          loading: true,
        }}
        secondaryButtonProps={{
          text: 'Cancel',
          onClick: () => navigate('/wallet/recipients'),
        }}
      >
        {isPollingTravelRule && (
          <Markdown
            markdown={`To comply with regulations, you need to provide information about the recipient. [Click here](${travelRuleUrl}) to go to the form.`}
            styles={{
              color: palette.grey.default,
              fontSize: '0.85rem',
            }}
          />
        )}
        {popupBlocked && (
          <Modal
            closeModal={() => {
              setPopupBlocked(false);
            }}
          >
            <div
              css={{
                display: 'grid',
                gap: spacing.xs,
                maxWidth: sizing.small,
                textAlign: 'center',
              }}
            >
              <Typography variant="bodySmall" color={palette.danger.default}>
                Travel rule form window blocked from opening, open the form and
                fill in all the details to continue
              </Typography>
              <Button
                onClick={() => {
                  setPopupBlocked(false);
                  openTravelRuleForm();
                }}
                text="Open travel rule form"
              />
            </div>
          </Modal>
        )}
      </Layout>
      {showSendError && travelRuleInfoError && (
        <Snackbar
          message={formatErrorResponse(travelRuleInfoError)}
          variant="danger"
          open={showSendError}
          onClose={() => {
            setShowSendError(false);
          }}
        />
      )}
    </LayoutModal>
  );
};
